// Import components
import GroupPriceInput from "./GroupInputPrice";
import SubVariantPriceInput from "./SubVariantPriceInput";
import GroupInventoryInput from "./GroupInventoryInput";
import SubVariantInventoryInput from "./SubVariantInventoryInput";
import VariantImageCell from "./VariantImageCell";

// Import lucid icons
import { ChevronDown, ChevronRight } from "lucide-react";

export default function VariantTableRows({
  comboList,
  setComboList,
  groupedCombos,
  isSingleOption,
  groupBy,
  expanded,
  selected,
  setSelected,
  setActiveVariant,
  toggleExpand,
  toggleSelect,
  groupImages = {},
  setGroupImages = () => {},
}) {

  const handleImageChange = (variantUiId, newImage) => {
    setComboList((prev) =>
      prev.map((combo) =>
        combo.variantUiId === variantUiId
          ? { ...combo, imageOverride: newImage ?? null }
          : combo
      )
    );
  };

  const handleGroupImageChange = (groupName, newImage) => {
    // Set an explicit image for the group so the parent row shows it
    setGroupImages((prev) => ({ ...prev, [groupName]: newImage ?? null }));
    // Apply this image to all sub-variants in the group
    setComboList((prev) =>
      prev.map((combo) =>
        combo.options?.[groupBy] === groupName
          ? { ...combo, imageOverride: newImage ?? null }
          : combo
      )
    );
  };

  return Object.entries(groupedCombos).map(([groupName, comboGroup]) => {
    const hasSubVariants = !isSingleOption && comboGroup.length >= 1;

    return (
      <div key={groupName}>
        <div 
          className="flex items-center gap-[1em] p-[0.5em] hover:bg-[#f5f5f5] cursor-pointer transition-colors duration-150"
          onClick={() => {
            if (!hasSubVariants) setActiveVariant(comboGroup[0]);
          }}
        >
          {/* Group checkbox */}
          <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              className="h-4 w-4 accent-black"
              checked={comboGroup.every((c) => selected.includes(c.variantUiId))}
              onChange={() => {
                const allSelected = comboGroup.every((c) =>
                  selected.includes(c.variantUiId)
                );
                setSelected((prev) =>
                  allSelected
                    ? prev.filter((id) => !comboGroup.find((c) => c.variantUiId === id))
                    : [...prev, ...comboGroup.map((c) => c.variantUiId)]
                );
              }}
            />
          </div>

          {/* Variant image and name */}
          <div className="flex-1 max-w-[320px] flex items-center gap-3">
            <VariantImageCell
              imageUrl={groupImages[groupName] ?? null}
              variantUiId={`group:${groupName}`}
              onImageChange={(/* id */ _, url) => handleGroupImageChange(groupName, url)}
              size="md"
            />

            {/* Variant name and sub-variants toggle */}
            <div className="flex flex-col gap-[0.4em]">
              <p className="text-[15px] font-medium" >{groupName}</p>
              {hasSubVariants && (
                <div className="flex gap-[0.5em] items-center text-[14px] font-medium">
                  <p onClick={(e) => e.stopPropagation()}>{comboGroup.length} sub-variants</p>
                  <button 
                    type="button" 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(groupName);
                    }} 
                  >
                    {expanded[groupName] ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Variant Price */}
          <GroupPriceInput
            groupName={groupName}
            comboGroup={comboGroup}
            groupBy={groupBy}
            comboList={comboList}
            setComboList={setComboList}
          />

          {/* Variant Inventory */}
          <GroupInventoryInput
            groupName={groupName}
            comboGroup={comboGroup}
            hasSubVariants={hasSubVariants}
            comboList={comboList}
            setComboList={setComboList}
          />
        </div>

        {/* Sub-variant rows */}
        {hasSubVariants && expanded[groupName] &&
          comboGroup.map((child) => (
            <div
              key={child.variantUiId}
              className="flex items-center gap-[1em] p-[0.5em] pl-[2.5em] hover:bg-[#f7f7f7] cursor-pointer transition-colors duration-150"
              onClick={() => setActiveVariant(child)}
            >
              {/* Checkbox */}
              <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-black"
                  checked={selected.includes(child.variantUiId)}
                  onChange={() => toggleSelect(child.variantUiId)}
                />
              </div>

              {/* sub variant image and name */}
              <div className="flex-1 max-w-[290px] flex items-center gap-3">
                <VariantImageCell
                  imageUrl={child.imageOverride}
                  variantUiId={child.variantUiId}
                  onImageChange={handleImageChange}
                  size="sm"
                />

                <div className="flex flex-col gap-[0.1em]">
                  <p className="text-[13px] font-medium">{child.subVariantName}</p>
                  {child.sku && (
                    <p className="text-[13px] text-[#636363] font-medium">{child.sku}</p>
                  )}
                </div>
              </div>
              
              {/* Sub variant price */}
              <SubVariantPriceInput
                variantUiId={child.variantUiId}
                comboList={comboList}
                setComboList={setComboList}
              />
              
              {/* Sub variant inventory */}
              <SubVariantInventoryInput
                variantUiId={child.variantUiId}
                comboList={comboList}
                setComboList={setComboList}
              />
            </div>
          ))}
      </div>
    );
  });
}
